import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useDispatch, useTranslation } from '@ufc/core';
import { authActions } from '@ufc/domains/auth';
import { initActions } from '@ufc/domains/portal-init';
import { exchangeHandoffToken, storeAuthSession } from '@/lib/sso-handoff-client';

/**
 * SSO Handoff Route
 * Receives handoff token from URL, exchanges it for JWT, and redirects
 * 
 * URL format: /sso/handoff?token=xxx
 */
export const Route = createFileRoute('/sso/handoff')({
  component: SSOHandoffPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || '',
    };
  },
});

const HANDOFF_DELAY_MS = 100;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function handleLoginRedirect(navigate: ReturnType<typeof useNavigate>): void {
  navigate({ to: '/login' });
}

async function handleHandoff(params: {
  token: string;
  navigate: ReturnType<typeof useNavigate>;
  dispatch: ReturnType<typeof useDispatch>;
  hasProcessedRef: MutableRefObject<boolean>;
  setError: Dispatch<SetStateAction<string | null>>;
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
}): Promise<void> {
  const {
    token,
    navigate,
    dispatch,
    hasProcessedRef,
    setError,
    setIsProcessing,
  } = params;

  // Prevent double execution (React Strict Mode in dev)
  if (hasProcessedRef.current) {
    return;
  }

  if (!token) {
    setError('No handoff token provided');
    setIsProcessing(false);
    return;
  }

  // Mark as processing to prevent race conditions
  hasProcessedRef.current = true;

  try {
    // Exchange token for JWT
    const result = await exchangeHandoffToken(token, 'customer');

    // Store JWT and user in localStorage (same keys as auth domain)
    storeAuthSession(result.jwt, result.user);

    // Sync auth state so ProtectedRoute doesn't require a refresh
    dispatch(authActions.loginSuccess({ user: result.user, token: result.jwt }));
    dispatch(initActions.initialize());

    // Wait a moment for storage event to propagate
    await sleep(HANDOFF_DELAY_MS);

    // Redirect to target page
    navigate({ to: '/' });
  } catch (err) {
    console.error('SSO handoff failed:', err);
    setError(err instanceof Error ? err.message : 'Failed to complete sign-in');
    setIsProcessing(false);
    // Reset ref on error so user can retry
    hasProcessedRef.current = false;
  }
}

function SSOHandoffPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    void handleHandoff({
      token,
      navigate,
      dispatch,
      hasProcessedRef,
      setError,
      setIsProcessing,
    });
  }, [token, navigate, dispatch]);

  const handleGoToLogin = useCallback(() => {
    handleLoginRedirect(navigate);
  }, [navigate]);

  if (isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-lg font-medium text-gray-700">{t('sso.signingIn')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-gray-50">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="rounded-full bg-red-100 p-3">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t('sso.signInFailed')}</h1>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={handleGoToLogin}
            className="mt-4 rounded-lg bg-primary px-6 py-2 text-white font-medium hover:bg-primary/90 transition-colors"
          >
            {t('sso.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
